"use client";

/**
 * Flash Sale 도메인 TanStack Query 훅
 * ✅ curl verified — slug 기반 상세 조회
 *
 * 레거시 패턴: getGroups() → 각 그룹별 returned-stocks 병렬 fetch → merge
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  createFlashSaleService,
  type FlashSaleGroup,
  type ReturnedStockFlat,
} from "@/shared/api/services/flashSaleService";
import { useBuyerService } from "@/shared/auth/hooks";

import { flashSaleKeys } from "@/features/flash-sale/queries/keys";

export interface FlashSaleGroupWithStocks extends FlashSaleGroup {
  returnedStocks: ReturnedStockFlat[];
}

export function useFlashSales() {
  const service = useBuyerService(createFlashSaleService);

  return useQuery({
    queryKey: flashSaleKeys.lists,
    queryFn: async (): Promise<FlashSaleGroupWithStocks[]> => {
      const groupsRes = await service.getFlashSales();
      const groups: FlashSaleGroup[] = groupsRes.data ?? [];

      // Parallel fetch returned stocks for each group (legacy N+1 pattern)
      const enriched = await Promise.all(
        groups.map(async (group) => {
          try {
            const stocksRes = await service.getReturnedStocks(group.id);
            return { ...group, returnedStocks: stocksRes.data ?? [] };
          } catch {
            return { ...group, returnedStocks: [] };
          }
        }),
      );

      return enriched;
    },
  });
}

/** 상품이 활성 깜짝할인에 포함되어 있으면 해당 slug를 반환 */
export function useProductFlashSaleInfo(productId: number | undefined) {
  const { data: groups } = useFlashSales();

  return useMemo(() => {
    if (!productId || !groups) return null;
    for (const group of groups) {
      if (group.status === "ended" || group.status === "sold") continue;
      const stock = group.returnedStocks.find((s) => s.product_id === productId);
      if (stock) {
        const discountRate = Number(group.discount_rate ?? 0);
        const originalPrice = stock.price ?? 0;
        return {
          slug: group.slug,
          discountRate,
          originalPrice,
          discountedPrice: Math.floor(originalPrice * (1 - discountRate / 100)),
          amount: stock.amount,
        };
      }
    }
    return null;
  }, [productId, groups]);
}

/** 상품이 활성 깜짝할인에 포함되어 있으면 해당 slug를 반환 */
export function useProductFlashSaleSlug(productId: number | undefined) {
  return useProductFlashSaleInfo(productId)?.slug ?? null;
}

export function useFlashSaleDetail(slug: string) {
  const service = useBuyerService(createFlashSaleService);

  return useQuery({
    queryKey: flashSaleKeys.detail(slug),
    queryFn: () => service.getFlashSaleDetail(slug),
    enabled: !!slug,
  });
}

/** 통째 구매 견적. zipCode를 주면 그 지역 배송비로 계산된다. */
export function useFlashSaleBulkQuote(
  zipCode = "",
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  const service = useBuyerService(createFlashSaleService);

  return useQuery({
    queryKey: flashSaleKeys.bulkQuote(zipCode),
    queryFn: async () => (await service.getBulkQuote(zipCode || undefined)).data,
    enabled,
    staleTime,
  });
}
